import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { Accordion } from 'react-bootstrap';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { TableActionButton } from '../styles/StyledComponents';

import { useAppContext } from '../contextApi/context';
import { getWorkOrder } from '../services/OrdersService';
import { getOrderStatusConfig } from '../constants/orderStatus';
import { getClientTypeLabel } from '../constants/orderClientTypes';
import { checkPermission } from '../utils/permissions';
import { formatDateAndTime, formatDateOnly } from '../utils/dates';
import { formatCurrency, formatHours } from '../utils/numbers';
import Spinner from '../components/shared/Spinner';
import StockMovementDetailsModal from '../components/shared/StockMovementDetailsModal';
import { getIssueMovements, getMovementDetails } from '../services/StockMovementService';

/**
 * Página de detalles de órdenes de trabajo
 * Muestra información completa de una orden específica en modo de solo lectura
 * Accesible desde la tabla de órdenes activas y el historial
 */
const OrderDetails = () => {
    // ----- Context/Hooks
    const location = useLocation();
    const navigate = useNavigate();
    const { loggedUser } = useAppContext();

    // Obtener orderId desde el state de navegación
    const orderId = location.state?.orderId;

    // ----- State
    const [currentOrder, setCurrentOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [activeKey, setActiveKey] = useState('0'); // Acordeón activo (por defecto el primero)

    // State - Despachos
    const [issueMovements, setIssueMovements] = useState([]);
    const [isLoadingIssues, setIsLoadingIssues] = useState(false);
    const [showIssueDetailsModal, setShowIssueDetailsModal] = useState(false);
    const [selectedIssueId, setSelectedIssueId] = useState(null);
    const [issueMovementDetails, setIssueMovementDetails] = useState({});
    const [loadingIssueDetails, setLoadingIssueDetails] = useState({});

    // ----- API Fetch
    const fetchOrderDetails = useCallback(
        async (id) => {
            setIsLoading(true);
            setLoadError(null);

            try {
                const response = await getWorkOrder(loggedUser.token, id);

                if (response.success) {
                    setCurrentOrder(response.data);
                } else {
                    setLoadError(response.error || 'No se pudo cargar la información de la orden');
                }
            } catch (err) {
                setLoadError('Error inesperado al cargar los detalles de la orden');
                console.error('Error fetching order details:', err);
            } finally {
                setIsLoading(false);
            }
        },
        [loggedUser.token]
    );

    /**
     * Obtiene los detalles específicos de un despacho (artículos, cantidades, etc.)
     */
    const fetchIssueMovementDetails = useCallback(
        async (movementId) => {
            if (!movementId || !loggedUser?.token) return;

            // Evitar cargar si ya está cargando o ya se cargó
            if (loadingIssueDetails[movementId] || issueMovementDetails[movementId]) return;

            setLoadingIssueDetails((prev) => ({ ...prev, [movementId]: true }));

            try {
                const response = await getMovementDetails(loggedUser.token, movementId);

                if (response) {
                    // Si hay devolución, cargar también esos detalles
                    if (response.returnMovement !== null) {
                        try {
                            const returnResponse = await getMovementDetails(loggedUser.token, response.returnMovement);
                            setIssueMovementDetails((prev) => ({
                                ...prev,
                                [movementId]: {
                                    ...response,
                                    returnMovementDetails: returnResponse,
                                },
                            }));
                        } catch (returnError) {
                            console.error(`Error al cargar detalles de devolución ${response.returnMovement}:`, returnError);
                            // Guardar sin detalles de devolución si falla
                            setIssueMovementDetails((prev) => ({
                                ...prev,
                                [movementId]: response,
                            }));
                        }
                    } else {
                        setIssueMovementDetails((prev) => ({
                            ...prev,
                            [movementId]: response,
                        }));
                    }
                }
            } catch (error) {
                console.error(`Error al cargar detalles del despacho ${movementId}:`, error);
            } finally {
                setLoadingIssueDetails((prev) => ({ ...prev, [movementId]: false }));
            }
        },
        [loggedUser.token, loadingIssueDetails, issueMovementDetails]
    );

    /**
     * Obtiene la lista de despachos asociados a la orden de trabajo
     */
    const fetchIssueMovements = useCallback(async () => {
        if (!orderId) return;

        setIsLoadingIssues(true);

        try {
            const filters = [
                {
                    columnName: 'workOrderId',
                    operation: 'equal',
                    value: [orderId],
                },
            ];

            const response = await getIssueMovements(loggedUser.token, 0, 100, 'eventDate,asc', filters);

            if (response && response.content) {
                setIssueMovements(response.content);
            } else {
                setIssueMovements([]);
            }
        } catch (error) {
            console.error('Error al cargar despachos:', error);
            setIssueMovements([]);
        } finally {
            setIsLoadingIssues(false);
        }
    }, [orderId, loggedUser.token]);

    // ----- Funciones para calcular totales de servicios
    const calculateInternalServicesTotal = () => {
        if (!currentOrder?.performedWorks) return 0;
        return currentOrder.performedWorks.reduce((total, service) => {
            const amount = service.totalAmount;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    const calculateExternalServicesTotal = () => {
        if (!currentOrder?.externalWorks) return 0;
        return currentOrder.externalWorks.reduce((total, service) => {
            const amount = service.totalAmount;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    const calculateDispatchesTotal = () => {
        return issueMovements.reduce((total, issue) => {
            const amount = issue.totalValueWithReturn;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    /**
     * Calcula el valor total de la orden sumando servicios internos, externos y despachos
     */
    const calculateOrderTotal = () => {
        const internalTotal = calculateInternalServicesTotal();
        const externalTotal = calculateExternalServicesTotal();
        const dispatchesTotal = calculateDispatchesTotal();

        return internalTotal + externalTotal + dispatchesTotal;
    };

    // ----- Handlers
    const handleShowIssueDetails = (id) => {
        setSelectedIssueId(id);
        setShowIssueDetailsModal(true);
    };

    const handleCloseIssueDetailsModal = () => {
        setSelectedIssueId(null);
        setShowIssueDetailsModal(false);
    };

    // Effect separado para cargar los detalles del primer despacho
    useEffect(() => {
        if (issueMovements.length > 0 && !isLoadingIssues) {
            const firstIssueId = issueMovements[0].id;
            // Verificar si ya se cargaron los detalles del primer despacho
            if (!issueMovementDetails[firstIssueId] && !loadingIssueDetails[firstIssueId]) {
                fetchIssueMovementDetails(firstIssueId);
            }
        }
    }, [issueMovements, isLoadingIssues, issueMovementDetails, loadingIssueDetails, fetchIssueMovementDetails]);

    // ----- Effects
    useEffect(() => {
        if (orderId && checkPermission(loggedUser.permissions, 'view_work_orders')) {
            fetchOrderDetails(orderId);
            fetchIssueMovements(); // Cargar despachos también
        }
    }, [orderId, fetchOrderDetails, fetchIssueMovements, loggedUser.permissions]);

    // Redirección si no tiene permisos
    useEffect(() => {
        if (!loggedUser || !checkPermission(loggedUser.permissions, 'view_work_orders')) {
            navigate('/ordenes');
        }
    }, [loggedUser, navigate]);

    // ----- Render
    // Verificación de permisos
    if (!loggedUser || !checkPermission(loggedUser.permissions, 'view_work_orders')) {
        return null;
    }

    if (isLoading) {
        return (
            <div className='container mt-4'>
                <div className='mb-3'>
                    <Link to='/ordenes'>Volver a gestión de órdenes</Link>
                </div>
                <div className='text-center mt-5'>
                    <Spinner />
                    <p className='mt-3'>Cargando información de la orden...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className='container mt-4'>
                <div className='mb-3'>
                    <Link to='/ordenes'>Volver a gestión de órdenes</Link>
                </div>
                <div className='alert alert-danger text-center mt-4' role='alert'>
                    <h4>Error al cargar la orden</h4>
                    <p>{loadError}</p>
                    <button className='btn btn-primary mt-3' onClick={() => fetchOrderDetails(orderId)}>
                        Intentar nuevamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='container mt-4'>
            <div className='mb-3'>
                <Link to='/ordenes'>Volver a gestión de órdenes</Link>
            </div>

            <HeaderContainer>
                <h1 className='mb-0'>Detalles de la Orden de Trabajo #{currentOrder?.orderNumber || ''}</h1>
                {currentOrder?.status && (
                    <StatusBadge
                        style={{
                            backgroundColor: getOrderStatusConfig(currentOrder.status).color,
                        }}
                    >
                        {getOrderStatusConfig(currentOrder.status).label}
                    </StatusBadge>
                )}
            </HeaderContainer>

            <DetailsContainer>
                {/* Información básica */}
                <InfoField>
                    <label className='fw-bold'>Fecha de solicitud:</label>
                    <span>{currentOrder?.requestedDate ? formatDateAndTime(currentOrder.requestedDate) : '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Solicitante:</label>
                    <span>{currentOrder?.requesterUser || '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Placa:</label>
                    <span>{currentOrder?.vehiclePlate || '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Kilometraje:</label>
                    <span>{currentOrder?.odometer || '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Tipo de cliente:</label>
                    <span>{currentOrder?.orderClientType ? getClientTypeLabel(currentOrder.orderClientType) : '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Tipo de mantenimiento:</label>
                    <span>{currentOrder?.maintenanceTypeName || '-'}</span>
                </InfoField>

                <InfoField>
                    <label className='fw-bold'>Valor total:</label>
                    <span>₡{formatCurrency(calculateOrderTotal())}</span>
                </InfoField>

                {/* Observaciones e instrucciones */}
                {currentOrder?.observations && (
                    <InfoField>
                        <label className='fw-bold'>Observaciones:</label>
                        <span>{currentOrder.observations}</span>
                    </InfoField>
                )}

                {currentOrder?.instructions && (
                    <InfoField>
                        <label className='fw-bold'>Instrucciones:</label>
                        <span>{currentOrder.instructions}</span>
                    </InfoField>
                )}

                {/* Fechas de mantenimiento */}
                {currentOrder?.maintenanceStartDate && (
                    <InfoField>
                        <label className='fw-bold'>Inicio de mantenimiento:</label>
                        <span>{formatDateAndTime(currentOrder.maintenanceStartDate)}</span>
                    </InfoField>
                )}

                {currentOrder?.maintenanceEndDate && (
                    <InfoField>
                        <label className='fw-bold'>Fin de mantenimiento:</label>
                        <span>{formatDateAndTime(currentOrder.maintenanceEndDate)}</span>
                    </InfoField>
                )}
            </DetailsContainer>

            <hr />

            {/* ACORDEÓN DE SECCIONES */}
            <AccordionWrapper activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                {/* SERVICIOS INTERNOS */}
                <Accordion.Item eventKey='0'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Servicios Internos</AccordionTitle>
                            {currentOrder?.performedWorks && currentOrder.performedWorks.length > 0 && (
                                <AccordionTotal>(₡{formatCurrency(calculateInternalServicesTotal())})</AccordionTotal>
                            )}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <ServicesContainer>
                            {currentOrder?.performedWorks && currentOrder.performedWorks.length > 0 ? (
                                <table className='table table-bordered'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Tipo</th>
                                            <th scope='col'>Subtipo</th>
                                            <th scope='col'>Descripción</th>
                                            <th scope='col'>Mecánico</th>
                                            <th scope='col'>Inicio</th>
                                            <th scope='col'>Fin</th>
                                            <th scope='col'>Horas</th>
                                            <th scope='col'>Monto total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrder.performedWorks.map((work) => (
                                            <tr key={work.id}>
                                                <td>{work.workTypeName}</td>
                                                <td>{work.workSubtypeName}</td>
                                                <td>
                                                    <DescriptionCell data-tooltip={work.description || ''}>
                                                        {work.description
                                                            ? work.description.length > 30
                                                                ? `${work.description.substring(0, 30)}...`
                                                                : work.description
                                                            : '-'}
                                                    </DescriptionCell>
                                                </td>
                                                <td>{work.mechanicName}</td>
                                                <td>{formatDateOnly(work.startTime)}</td>
                                                <td>{work.endTime ? formatDateOnly(work.endTime) : 'En proceso'}</td>
                                                <td>{formatHours(work.hoursSpent)}</td>
                                                <td>
                                                    {work.totalAmount !== null && work.totalAmount !== undefined
                                                        ? `₡${formatCurrency(work.totalAmount)}`
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-muted'>No hay servicios internos registrados para esta orden.</p>
                            )}
                        </ServicesContainer>
                    </Accordion.Body>
                </Accordion.Item>

                {/* SERVICIOS EXTERNOS */}
                <Accordion.Item eventKey='1'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Servicios Externos</AccordionTitle>
                            {currentOrder?.externalWorks && currentOrder.externalWorks.length > 0 && (
                                <AccordionTotal>(₡{formatCurrency(calculateExternalServicesTotal())})</AccordionTotal>
                            )}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <ServicesContainer>
                            {currentOrder?.externalWorks && currentOrder.externalWorks.length > 0 ? (
                                <table className='table table-bordered'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Tipo</th>
                                            <th scope='col'>Subtipo</th>
                                            <th scope='col'>Descripción</th>
                                            <th scope='col'>Proveedor</th>
                                            <th scope='col'>Inicio</th>
                                            <th scope='col'>Fin</th>
                                            <th scope='col'>Horas</th>
                                            <th scope='col'>Monto total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrder.externalWorks.map((work) => (
                                            <tr key={work.id}>
                                                <td>{work.workTypeName}</td>
                                                <td>{work.workSubtypeName}</td>
                                                <td>
                                                    <DescriptionCell data-tooltip={work.description || ''}>
                                                        {work.description
                                                            ? work.description.length > 30
                                                                ? `${work.description.substring(0, 30)}...`
                                                                : work.description
                                                            : '-'}
                                                    </DescriptionCell>
                                                </td>
                                                <td>{work.mechanicName}</td>
                                                <td>{formatDateOnly(work.startTime)}</td>
                                                <td>{work.endTime ? formatDateOnly(work.endTime) : 'En proceso'}</td>
                                                <td>{formatHours(work.hoursSpent)}</td>
                                                <td>
                                                    {work.totalAmount !== null && work.totalAmount !== undefined
                                                        ? `₡${formatCurrency(work.totalAmount)}`
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-muted'>No hay servicios externos registrados para esta orden.</p>
                            )}
                        </ServicesContainer>
                    </Accordion.Body>
                </Accordion.Item>

                {/* SECCIÓN DE DESPACHOS */}
                <Accordion.Item eventKey='2'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Despachos</AccordionTitle>
                            {issueMovements.length > 0 && <AccordionTotal>(₡{formatCurrency(calculateDispatchesTotal())})</AccordionTotal>}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <DispatchesContainer>
                            {isLoadingIssues ? (
                                <div className='text-center py-4'>
                                    <div className='spinner-border text-primary' role='status'>
                                        <span className='visually-hidden'>Cargando despachos...</span>
                                    </div>
                                </div>
                            ) : issueMovements.length > 0 ? (
                                <Accordion alwaysOpen defaultActiveKey={issueMovements.length > 0 ? `dispatch-${issueMovements[0].id}` : undefined}>
                                    {issueMovements.map((issue, index) => {
                                        const dispatchNumber = index + 1;
                                        const movementDetails = issueMovementDetails[issue.id];
                                        const isLoadingDetails = loadingIssueDetails[issue.id];

                                        return (
                                            <Accordion.Item eventKey={`dispatch-${issue.id}`} key={issue.id}>
                                                <Accordion.Header onClick={() => fetchIssueMovementDetails(issue.id)}>
                                                    <div className='d-flex justify-content-between align-items-center w-100 me-3'>
                                                        <div className='flex-grow-1'>
                                                            <DispatchTitle>Despacho #{dispatchNumber}</DispatchTitle>
                                                            <div className='row'>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Fecha:</strong>{' '}
                                                                        {issue.eventDate ? formatDateAndTime(issue.eventDate) : '--'}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Despachador:</strong> {issue.user}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Solicitante:</strong> {issue.receivingUser}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Valor total:</strong> ₡{formatCurrency(issue.totalValueWithReturn)}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='ms-2 d-flex gap-2'>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-primary'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleShowIssueDetails(issue.id);
                                                                }}
                                                            >
                                                                <FaMagnifyingGlass />
                                                                Detalles
                                                            </TableActionButton>
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className='px-3 py-2'>
                                                        {/* Descripción si existe */}
                                                        {issue.description && (
                                                            <div className='mb-3'>
                                                                <strong>Descripción:</strong> {issue.description}
                                                            </div>
                                                        )}

                                                        <hr />

                                                        {/* Artículos del despacho */}
                                                        {isLoadingDetails ? (
                                                            <div className='text-center py-3'>
                                                                <div className='spinner-border spinner-border-sm text-primary' role='status'>
                                                                    <span className='visually-hidden'>Cargando detalles...</span>
                                                                </div>
                                                                <p className='mt-2 mb-0 text-muted'>Cargando artículos del despacho...</p>
                                                            </div>
                                                        ) : movementDetails && movementDetails.details ? (
                                                            <>
                                                                <h6 className='fw-bold mb-3'>Artículos despachados:</h6>
                                                                <div className='table-responsive'>
                                                                    <table className='table table-bordered table-sm'>
                                                                        <thead className='table-light'>
                                                                            <tr>
                                                                                <th scope='col'>Artículo</th>
                                                                                <th scope='col'>Marca</th>
                                                                                <th scope='col'>Cantidad</th>
                                                                                <th scope='col'>Precio unitario</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {movementDetails.details.map((item, itemIndex) => (
                                                                                <tr key={itemIndex}>
                                                                                    <td>{item.isService ? item.serviceConcept : item.articleName}</td>
                                                                                    <td>{item.isService ? '--' : item.brand}</td>
                                                                                    <td>{item.amount}</td>
                                                                                    <td>₡{formatCurrency(item.itemsValue)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>

                                                                {/* Valor total del despacho */}
                                                                <div className='text-end mb-3'>
                                                                    <div className='d-inline-block p-2 rounded border'>
                                                                        <strong>
                                                                            Valor total del despacho: ₡{formatCurrency(movementDetails.totalValue)}
                                                                        </strong>
                                                                    </div>
                                                                </div>

                                                                {/* Mostrar devolución si existe */}
                                                                {movementDetails.returnMovementDetails && (
                                                                    <>
                                                                        <hr />
                                                                        <h6 className='fw-bold mb-3'>Artículos devueltos:</h6>
                                                                        <div className='table-responsive'>
                                                                            <table className='table table-bordered table-sm'>
                                                                                <thead className='table-light'>
                                                                                    <tr>
                                                                                        <th scope='col'>Artículo</th>
                                                                                        <th scope='col'>Marca</th>
                                                                                        <th scope='col'>Cantidad</th>
                                                                                        <th scope='col'>Precio unitario</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {movementDetails.returnMovementDetails.details.map(
                                                                                        (item, itemIndex) => (
                                                                                            <tr key={itemIndex}>
                                                                                                <td>
                                                                                                    {item.isService
                                                                                                        ? item.serviceConcept
                                                                                                        : item.articleName}
                                                                                                </td>
                                                                                                <td>{item.isService ? '--' : item.brand}</td>
                                                                                                <td>{item.amount}</td>
                                                                                                <td>₡{formatCurrency(item.itemsValue)}</td>
                                                                                            </tr>
                                                                                        )
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>

                                                                        {/* Valor total de la devolución */}
                                                                        <div className='text-end mb-3'>
                                                                            <div className='d-inline-block p-2 rounded border'>
                                                                                <strong>
                                                                                    Valor total de la devolución: ₡
                                                                                    {formatCurrency(movementDetails.returnMovementDetails.totalValue)}
                                                                                </strong>
                                                                            </div>
                                                                        </div>

                                                                        <hr />

                                                                        {/* Valor total final */}
                                                                        <div className='text-end mb-3'>
                                                                            <div className='d-inline-block p-2 rounded border'>
                                                                                <strong>
                                                                                    Valor total final: ₡
                                                                                    {formatCurrency(
                                                                                        movementDetails.totalValue -
                                                                                            movementDetails.returnMovementDetails.totalValue
                                                                                    )}
                                                                                </strong>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className='text-center py-3'>
                                                                <p className='text-muted mb-2'>No se pudieron cargar los detalles del despacho.</p>
                                                                <button
                                                                    className='btn btn-sm btn-outline-primary'
                                                                    onClick={() => fetchIssueMovementDetails(issue.id)}
                                                                >
                                                                    Reintentar
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            ) : (
                                <p className='text-muted'>No hay despachos registrados para esta orden de trabajo.</p>
                            )}
                        </DispatchesContainer>
                    </Accordion.Body>
                </Accordion.Item>
            </AccordionWrapper>

            {/* Modal de detalles de despacho */}
            {showIssueDetailsModal && (
                <StockMovementDetailsModal movementId={selectedIssueId} closeHandler={handleCloseIssueDetailsModal} show={showIssueDetailsModal} />
            )}
        </div>
    );
};

// Styled Components
const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    h1 {
        margin: 0;
    }
`;

const StatusBadge = styled.span`
    padding: 8px 16px;
    border-radius: 50px;
    color: #fff;
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
`;

const DetailsContainer = styled.div`
    margin: 25px auto 0;
    padding: 40px 40px 20px;
    width: 70%;
`;

const InfoField = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 15px;

    label {
        font-size: 15px;
        margin: 0;
        min-width: 200px;
    }

    span {
        font-size: 15px;
        color: #495057;
    }
`;

const ServicesContainer = styled.div`
    padding: 15px;

    p.text-muted {
        font-style: italic;
        color: #6c757d;
    }
`;

const DispatchesContainer = styled.div`
    padding: 15px;

    p.text-muted {
        font-style: italic;
        color: #6c757d;
    }
`;

const DescriptionCell = styled.div`
    max-width: 150px;
    position: relative;
    cursor: pointer;

    /* Solo mostrar tooltip si data-tooltip tiene contenido */
    &[data-tooltip]:not([data-tooltip='']):hover::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #333;
        color: #fff;
        padding: 8px 12px;
        border-radius: 6px;
        white-space: normal;
        width: max-content;
        max-width: 300px;
        font-size: 13px;
        font-weight: normal;
        line-height: 1.4;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &[data-tooltip]:not([data-tooltip='']):hover::after {
        content: '';
        position: absolute;
        bottom: 115%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #333;
        z-index: 1000;
    }
`;

const AccordionWrapper = styled(Accordion)`
    margin-top: 20px;

    .accordion-item {
        border: 1px solid #dee2e6;
        margin-bottom: 10px;
        border-radius: 4px;
    }

    .accordion-button {
        font-size: 16px;
        font-weight: 500;
        padding: 15px 20px;

        &:not(.collapsed) {
            background-color: #f8f9fa;
            color: #000;
            box-shadow: none;
        }

        &:focus {
            box-shadow: none;
            border-color: #dee2e6;
        }
    }

    .accordion-body {
        padding: 0;
    }
`;

const AccordionTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
`;

const AccordionTitle = styled.span`
    font-size: 24px;
    font-weight: 600;
`;

const AccordionTotal = styled.span`
    font-size: 18px;
    font-weight: 500;
    color: #495057;
`;

const DispatchTitle = styled.h6`
    font-size: 1.1rem !important;
    margin-bottom: 0.5rem;
    font-weight: bold;
`;

export default OrderDetails;
